/**
 * `useDemoSolveChanges` - GET /api/demo-sessions/current/solve-changes (#2824).
 *
 * What the visitor's Solve changed against the team as it was: the cells that
 * moved, the counts and a few plain lines. It reads only after a Solve ran for
 * the scenario now shown (`solved_current`), and the demo state read keeps that
 * flag fresh: a Solve and a new change both read it again.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { subscribeDemoGuideAction } from "../components/demo/guide/events";

import { readDemoSession } from "../lib/demoSession";
import { apiFetch } from "./client";
import type { components } from "./schema.gen";
import { useDemoSessionState } from "./useDemoSessionState";

export type DemoSolveChanges = components["schemas"]["DemoSolveChangesResponse"];

/**
 * True from a reported Solve until the demo state says the scenario is solved:
 * the card reads its changes and shows at once, before the state read is back.
 */
export function useJustSolved(): boolean {
  const [just, setJust] = useState(false);
  const solved = useDemoSessionState().data?.solved_current === true;
  useEffect(
    () =>
      subscribeDemoGuideAction((action) => {
        if (action === "solved") setJust(true);
      }),
    [],
  );
  useEffect(() => {
    if (solved) setJust(false);
  }, [solved]);
  useEffect(() => {
    if (!just) return;
    const timer = window.setTimeout(() => setJust(false), 8000);
    return () => window.clearTimeout(timer);
  }, [just]);
  return just;
}

/** When this tab last saw a Solve end. A module value, so a card mounted after the
 * event still knows a Solve just ran. */
let lastSolvedAt = 0;
subscribeDemoGuideAction((action) => {
  if (action === "solved") lastSolvedAt = Date.now();
});

/** How long after a Solve the changes are asked for again until the server has them. */
const SOLVE_POLL_MS = 20_000;

export function useDemoSolveChanges(justSolved = false): DemoSolveChanges | null {
  const token = readDemoSession()?.token ?? null;
  const state = useDemoSessionState();
  const solved = state.data?.solved_current === true;
  // A demo Solve ends in the background: the state read can come back before the
  // solve is recorded, so right after a Solve the changes are read again every
  // second until they say solved (#2824).
  const recent = justSolved || Date.now() - lastSolvedAt < SOLVE_POLL_MS;
  const query = useQuery<DemoSolveChanges>({
    queryKey: ["demo-session", "solve-changes", token, state.data?.variant ?? null],
    queryFn: () => apiFetch<DemoSolveChanges>("/api/demo-sessions/current/solve-changes"),
    enabled: token !== null && (solved || recent),
    retry: false,
    staleTime: 30_000,
    refetchInterval: (q) =>
      q.state.data?.solved || Date.now() - lastSolvedAt >= SOLVE_POLL_MS ? false : 1000,
  });
  if (!query.data?.solved) return null;
  return query.data;
}

/** True when a Solve ran in this tab a short while ago and its changes are still on the way. */
export function solveChangesStillComing(): boolean {
  return Date.now() - lastSolvedAt < SOLVE_POLL_MS;
}
